import React from "react";
import type { ActivitySearchResult, TaktColorEntry } from "../../../models";
import ActivitySearch from "../ActivitySearch/ActivitySearch";
import ZoomSlider from "../ZoomSlider/ZoomSlider";
import "./TaktToolbar.css";

interface Props {
  /* locate */
  scheduleId: number | null;
  activeColors: Record<string, TaktColorEntry>;
  onSearchSelect: (r: ActivitySearchResult) => void;
  /* View rail (filters + coloring) toggle */
  filtersOpen: boolean;
  onToggleFilters: () => void;
  activeFilterCount: number;
  /* zoom (continuous) */
  pxPerDay: number;
  defaultPx: number;
  zoomReadout: string;
  onZoom: (n: number) => void;
  /* export */
  onExport: () => void;
  onOpenTaktEditor: () => void;
}

const DownloadIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

/** Sliders icon — the View control covers both filtering and coloring. */
const SlidersIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
    <circle cx="16" cy="7" r="2" fill="currentColor" stroke="none" />
    <circle cx="8" cy="17" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const TaktToolbar: React.FC<Props> = (props) => {
  const {
    scheduleId,
    activeColors,
    onSearchSelect,
    filtersOpen,
    onToggleFilters,
    activeFilterCount,
    pxPerDay,
    defaultPx,
    zoomReadout,
    onZoom,
    onExport,
    onOpenTaktEditor,
  } = props;

  return (
    <div className="takt-toolbar">
      <div className="takt-toolbar__group">
        <button
          type="button"
          className={`takt-tool-btn${filtersOpen ? " takt-tool-btn--active" : ""}`}
          onClick={onToggleFilters}
          aria-pressed={filtersOpen}
          title="Filter + color the board"
        >
          <span className="takt-tool-btn__icon">
            <SlidersIcon />
          </span>
          View
          {activeFilterCount > 0 && (
            <span className="takt-tool-btn__badge">{activeFilterCount}</span>
          )}
        </button>
        <button
          type="button"
          className="takt-tool-btn"
          onClick={onOpenTaktEditor}
          title="Edit Takt structure & work codes"
        >
          <span className="takt-tool-btn__icon">
            <EditIcon />
          </span>
          Edit Board
        </button>
      </div>

      <div className="takt-toolbar__group">
        {scheduleId && (
          <ActivitySearch
            scheduleId={scheduleId}
            colors={activeColors}
            onSelect={onSearchSelect}
          />
        )}
        <ZoomSlider
          pxPerDay={pxPerDay}
          defaultPx={defaultPx}
          onZoom={onZoom}
          readout={zoomReadout}
        />
      </div>

      <div className="takt-toolbar__spacer" />

      <button
        type="button"
        className="takt-tool-btn takt-tool-btn--icon takt-tool-btn--export"
        onClick={onExport}
        aria-label="Export lookahead to PDF"
        title="Export lookahead to PDF"
      >
        <span className="takt-tool-btn__icon">
          <DownloadIcon />
        </span>
      </button>
    </div>
  );
};

export default TaktToolbar;
